import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  MenuItem,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { Event } from '../../types';
import apiService from '../../services/api';
import { toast } from 'react-toastify';

import { viewer } from '../../types/user.type';
import FormTextField from '../common/FormTextField';

interface viewerEditDialogProps {
  open: boolean;
  user: viewer | null;
  onClose: () => void;
  onSave: () => void;
}

export interface viewerRegisterType {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  userType: string;
  designation: string;
  companyName: string;
  areaOfSpecialization: string;
  eventId: number;
  attendanceReason: string;
  howDidYouKnow: string;
}

/////////satic value//////////////////
const howYouKnowOptions = [
  { id: 1, label: 'Facebook' },
  { id: 2, label: 'Instagram' },
  { id: 3, label: 'WhatsApp' },
  { id: 4, label: 'Friends / Family' },
  { id: 5, label: 'Email' },
  { id: 6, label: 'Other' },
];

const VisitorsEditDialog: React.FC<viewerEditDialogProps> = ({
  open,
  user,
  onClose,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [events, setEvents] = useState<Event[]>([]);

  const { handleSubmit, reset, control } = useForm<viewerRegisterType>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      userType: 'Visitor',
      designation: '',
      companyName: '',
      areaOfSpecialization: '',
      eventId: undefined,
      attendanceReason: '',
      howDidYouKnow: '',
    },
  });

  // populate form when user changes (edit mode)
  useEffect(() => {
    if (open && user) {
      reset({
        // eventName: event.eventName,
        // eventCode: event.eventCode || '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        userType: 'Visitor',
        designation: '',
        companyName: '',
        areaOfSpecialization: '',
        eventId: undefined,
        attendanceReason: '',
        howDidYouKnow: '',
      });
    } else if (open && !user) {
      // create mode
      reset({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        userType: 'Visitor',
        designation: '',
        companyName: '',
        areaOfSpecialization: '',
        eventId: undefined,
        attendanceReason: '',
        howDidYouKnow: '',
      });
    }
  }, [user, open]);

  useEffect(() => {
    const getAllEvents = async () => {
      try {
        const res = await apiService.getEvents();
        console.log('res', res);
        setEvents(res.data || []);
      } catch (error) {
        console.error(error);
        toast.error('Failed to fetch events');
      }
    };
    getAllEvents();
  }, []);

  ////////////////////// Submit (all data as FormData)///////////////////////////
  const onSubmit = async (data: viewerRegisterType) => {
    console.log('data', data);
    // console.log('updateData', updateData)
    setError('');
    setLoading(true);

    try {
      const updateData: viewerRegisterType = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        password: data.password,
        userType: 'Visitor',
        designation: data.designation,
        companyName: data.companyName,
        areaOfSpecialization: data.areaOfSpecialization,
        eventId: data.eventId,
        attendanceReason: data.attendanceReason,
        howDidYouKnow: data.howDidYouKnow,
      };

      // send data in api as multipart/form-data
      // if (event) {
      //     await apiService.updateEvent(event.id, formData);
      //     toast.success('Event updated successfully');
      // } else {
      await apiService.createViewer(updateData);
      toast.success('Visitor created successfully');
      // }

      reset();
      onSave();
      onClose();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${user ? 'update' : 'create'} visitor`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError('');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            {user ? 'Edit Visitor' : 'Create New Visitor'}
          </Typography>
          {/* {event && (
                        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                            {event.eventName}
                        </Typography>
                    )} */}
        </Box>
        <IconButton onClick={handleClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* first name */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="firstName"
                control={control}
                label="First Name"
                rules={{
                  required: 'First name is required',
                  minLength: { value: 2, message: 'Minimum 2 characters' },
                }}
              />
            </Grid>

            {/* last name */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="lastName"
                control={control}
                label="Last Name"
                rules={{
                  required: 'Last name is required',
                  minLength: { value: 2, message: 'Minimum 2 characters' },
                }}
              />
            </Grid>

            {/* email */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="email"
                control={control}
                label="Email"
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email',
                  },
                }}
              />
            </Grid>

            {/* password */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="password"
                control={control}
                label="password"
                type="password"
                rules={{
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Minimum 6 characters required',
                  },
                }}
              />
            </Grid>

            {/* phone number */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="phone"
                control={control}
                label="Contact Number"
                rules={{
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: 'Enter a valid 10 digit number',
                  },
                }}
              />
            </Grid>

            {/* designation */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="designation"
                control={control}
                label="Designation"
                rules={{
                  required: 'Designation is required',
                  minLength: { value: 2, message: 'Minimum 2 characters' },
                }}
              />
            </Grid>

            {/* company name */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="companyName"
                control={control}
                label="Company Name"
                rules={{
                  required: 'Company name is required',
                  minLength: { value: 2, message: 'Minimum 2 characters' },
                }}
              />
            </Grid>

            {/* Area of Specialization */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="areaOfSpecialization"
                control={control}
                label="Area of Specialization"
                rules={{
                  required: 'Area of specialization is required',
                  minLength: { value: 2, message: 'Minimum 2 characters' },
                }}
              />
            </Grid>

            {/* event(dropdown) */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="eventId"
                control={control}
                label="Select Event"
                select
                rules={{ required: 'Event is required' }}
              >
                {events.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.eventName}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>

            {/* how did you know (static dropdown) */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="howDidYouKnow"
                control={control}
                label="How did you come to know about this event?"
                select
                rules={{ required: 'Please select an option' }}
              >
                {howYouKnowOptions.map(item => (
                  <MenuItem key={item.label} value={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>

            {/* reason for attending the event  */}
            <Grid item xs={12}>
              <FormTextField
                name="attendanceReason"
                control={control}
                label="Reason for Attending the Event?"
                rules={{
                  required: 'Reason for Attending is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters',
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {loading
              ? user
                ? 'Updating...'
                : 'Creating...'
              : user
                ? 'Update Visitor'
                : 'Create Visitor'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default VisitorsEditDialog;
